import { ChevronDown, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { cn } from "@/utils";

/**
 * Shared collapse/expand state for the dashboard regions (Category scoring,
 * Funding Type Analysis, To-do tasks). Sections live in different columns and
 * component subtrees, so an "expand/collapse all" control needs lifted state.
 */
type TCollapsibleSectionsContextValue = {
  isCollapsed: (id: string) => boolean;
  toggle: (id: string) => void;
  setAll: (collapsed: boolean) => void;
  /** True while at least one known section is collapsed */
  anyCollapsed: boolean;
};

const CollapsibleSectionsContext = createContext<TCollapsibleSectionsContextValue | null>(null);

type TCollapsibleSectionsProviderProps = {
  /** Ids of every section the "expand/collapse all" button should control */
  sectionIds: string[];
  /** Sections start collapsed unless set to false */
  defaultCollapsed?: boolean;
  children: ReactNode;
};

export function CollapsibleSectionsProvider({
  sectionIds,
  defaultCollapsed = true,
  children,
}: TCollapsibleSectionsProviderProps) {
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const sectionIdsRef = useRef(sectionIds);
  sectionIdsRef.current = sectionIds;

  const isCollapsed = useCallback(
    (id: string) => overrides[id] ?? defaultCollapsed,
    [overrides, defaultCollapsed]
  );

  const toggle = useCallback(
    (id: string) => {
      setOverrides((prev) => ({ ...prev, [id]: !(prev[id] ?? defaultCollapsed) }));
    },
    [defaultCollapsed]
  );

  const setAll = useCallback((collapsed: boolean) => {
    setOverrides(Object.fromEntries(sectionIdsRef.current.map((id) => [id, collapsed])));
  }, []);

  const anyCollapsed = sectionIds.some((id) => overrides[id] ?? defaultCollapsed);

  const value = useMemo(
    () => ({ isCollapsed, toggle, setAll, anyCollapsed }),
    [isCollapsed, toggle, setAll, anyCollapsed]
  );

  return <CollapsibleSectionsContext.Provider value={value}>{children}</CollapsibleSectionsContext.Provider>;
}

type TCollapsibleSectionProps = {
  /** Stable id used by the provider / expand-all button */
  id: string;
  title: string;
  /** Optional extra header content, e.g. an item-count badge */
  badge?: ReactNode;
  children: ReactNode;
  className?: string;
};

/**
 * A titled dashboard region that can be collapsed/expanded. Uses the shared
 * provider state when available so the "expand/collapse all" button works;
 * falls back to local state (collapsed by default) otherwise.
 */
export function CollapsibleSection({ id, title, badge, children, className }: TCollapsibleSectionProps) {
  const ctx = useContext(CollapsibleSectionsContext);
  const [localCollapsed, setLocalCollapsed] = useState(true);
  const collapsed = ctx ? ctx.isCollapsed(id) : localCollapsed;
  const toggle = () => (ctx ? ctx.toggle(id) : setLocalCollapsed((prev) => !prev));
  const contentId = `collapsible-section-${id}`;

  return (
    <section className={cn("rounded-lg border border-border bg-card", className)}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={toggle}
            aria-expanded={!collapsed}
            aria-controls={contentId}
            className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-sm font-semibold text-foreground sm:text-base">{title}</span>
              {badge}
            </span>
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                !collapsed && "rotate-180"
              )}
              aria-hidden="true"
            />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-64 text-xs">
          {collapsed ? `Click to expand and see the ${title} details.` : `Click to collapse ${title}.`}
        </TooltipContent>
      </Tooltip>
      {!collapsed && (
        <div
          id={contentId}
          className="border-t border-border p-3"
        >
          {children}
        </div>
      )}
    </section>
  );
}

/**
 * Toggles every registered section at once. Renders nothing outside a
 * CollapsibleSectionsProvider.
 */
export function ExpandCollapseAllButton({ className }: { className?: string }) {
  const ctx = useContext(CollapsibleSectionsContext);
  if (!ctx) return null;

  const { anyCollapsed, setAll } = ctx;
  const Icon = anyCollapsed ? ChevronsUpDown : ChevronsDownUp;

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => setAll(!anyCollapsed)}
      className={cn("h-8 gap-1.5 px-3 text-xs", className)}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {anyCollapsed ? "Expand all" : "Collapse all"}
    </Button>
  );
}
